<template>
  <div class="row">
    <div v-if="geoZone && !isNewZone" class="row">
      <!-- Map -->
      <div class="col-12">
        <div class="card">
          <div id="map" style="height: 45vh; width: 100%;"></div>
        </div>
      </div>
    </div>

    <EntityForm :entity-id="geoZoneId" v-model:entity="geoZone"
      v-model:editState="isEditingGeoZone" form-name="Geo Zone" api-url="/api/zone"
      redirect-url="/admin/geozones" model-constructor="GeoZone" v-bind:show-title=true
      :post-save="handlePostSave">

      <!-- Wait until `geoZone` is defined before rendering the form -->
      <div v-if="geoZone.state">
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="name" class="form-label">Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" id="name" v-model="geoZone.state.name"
                :disabled="!isEditingGeoZone" :readonly="!isEditingGeoZone" required>
              <div id="name-error" class="invalid-feedback d-none">Please enter a name.</div>
            </div>

            <div class="mb-3">
              <label for="description" class="form-label">Description</label>
              <textarea class="form-control" id="description" v-model="geoZone.state.description" rows="3"
                :disabled="!isEditingGeoZone" :readonly="!isEditingGeoZone"></textarea>
            </div>

            <div class="mb-3">
              <label for="zoneType" class="form-label">Zone Type <span class="text-danger">*</span></label>
              <select class="form-select" id="zoneType" v-model="geoZone.state.zoneType"
                :disabled="!isEditingGeoZone" required>
                <option value="SERVICE_AREA">Service Area</option>
                <option value="SURGE_PRICING">Surge Pricing</option>
                <option value="RESTRICTED">Restricted</option>
                <option value="FARE_CONFIG_ZONE">Fare Config</option>
              </select>
              <div id="zoneType-error" class="invalid-feedback d-none">Please select a zone type.</div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-3">
              <label for="priority" class="form-label">Priority</label>
              <input type="number" class="form-control" id="priority" v-model="geoZone.state.priority" min="0"
                :disabled="!isEditingGeoZone" :readonly="!isEditingGeoZone">
            </div>

            <div class="mb-3">
              <label for="active" class="form-label">Status</label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="active" v-model="geoZone.state.active"
                  :disabled="!isEditingGeoZone">
                <label class="form-check-label" for="active">
                  {{ geoZone.state.active ? 'Active' : 'Inactive' }}
                </label>
              </div>
            </div>

            <div class="mb-3">
              <label for="boundaryWkt" class="form-label">Boundary Data (WKT) <span class="text-danger">*</span></label>
              <textarea class="form-control" id="boundaryWkt" v-model="geoZone.state.boundaryWkt" rows="5"
                :disabled="!isEditingGeoZone" :readonly="!isEditingGeoZone" required></textarea>
              <div id="boundaryWkt-error" class="invalid-feedback d-none">Please enter boundary data.</div>
            </div>

            <div class="mb-3" v-if="isEditingGeoZone">
              <label for="geojsonInput" class="form-label">GeoJSON Input</label>
              <textarea class="form-control" id="geojsonInput" v-model="geojsonInput" rows="5"
                placeholder="Paste GeoJSON here"></textarea>
              <div class="d-flex justify-content-between mt-2">
                <button type="button" class="btn btn-primary" @click="validateGeoJson">Validate & Convert to WKT</button>
                <button type="button" class="btn btn-outline-secondary" @click="loadSampleGeoJson">Load Sample</button>
              </div>
            </div>

            <div class="mb-3" v-if="isEditingGeoZone">
              <div class="alert alert-light-info">
                <h5>Finding Boundary Data:</h5>
                <ol>
                  <li>Search for a place on <a href="https://www.openstreetmap.org/" target="_blank">OpenStreetMap</a></li>
                  <li>Get the relationId from the URL</li>
                  <li>Replace the relationId in <a href="https://overpass-turbo.eu/" target="_blank">Overpass Turbo</a></li>
                  <li>Export as GeoJSON and paste in the GeoJSON section above for validation</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

      </div>
    </EntityForm>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, defineProps } from 'vue';
import EntityForm from './EntityForm.vue';
import {notificationType, showToast} from "../utility/toastNotificationUtility";
import {newEntityId} from '../globals.js';

const props = defineProps({
  googleMapsApiKey: {
    type: String,
    required: true
  },
  geoZoneId: {
    type: String,
    default: null
  }
});


// Reactive variables for Geo Zone
const geoZone = ref({});
const isNewZone = computed(() => props.geoZoneId === 'add' || props.geoZoneId === newEntityId);
const isEditingGeoZone = ref(false);
const geojsonInput = ref('');

// Google Maps variables
let map = null;
let polygon = null;

// Initialize Google Maps
const initMap = () => {
  if (!geoZone.value || !geoZone.value.state) return;

  // Wait for the DOM to be ready
  setTimeout(() => {
    const mapElement = document.getElementById('map');
    if (!mapElement) return;

    // Initialize the map
    map = new google.maps.Map(mapElement, {
      center: { lat: 0, lng: 0 },
      zoom: 10,
      mapTypeId: 'roadmap'
    });

    // Draw the polygon if boundary data exists
    if (geoZone.value.state.boundaryWkt) {
      drawPolygon(geoZone.value.state.boundaryWkt);
    }
  }, 100);
};

// Draw polygon on the map
const drawPolygon = (boundaryJson) => {
  if (!map) return;

  // Clear existing polygon
  if (polygon) {
    polygon.setMap(null);
  }

  try {
    // Parse the JSON string to get coordinates
    let coordinates = [];
    try {
      // Try to parse as JSON first
      const boundaryData = JSON.parse(boundaryJson);
      if (Array.isArray(boundaryData)) {
        coordinates = boundaryData.map(coord => ({
          lat: parseFloat(coord.lat || coord.latitude),
          lng: parseFloat(coord.lng || coord.longitude)
        }));
      }
    } catch (e) {
      // If not valid JSON, try to parse as WKT
      // This is a simple WKT parser for POLYGON format
      const wktMatch = boundaryJson.match(/POLYGON\s*\(\((.*)\)\)/i);
      if (wktMatch && wktMatch[1]) {
        coordinates = wktMatch[1].split(',').map(pair => {
          const [lng, lat] = pair.trim().split(' ').map(parseFloat);
          return { lat, lng };
        });
      }
    }

    if (coordinates.length === 0) {
      console.error('Invalid boundary format');
      return;
    }

    // Create the polygon
    polygon = new google.maps.Polygon({
      paths: coordinates,
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.35
    });

    polygon.setMap(map);

    // Fit the map to the polygon bounds
    const bounds = new google.maps.LatLngBounds();
    coordinates.forEach(coord => {
      bounds.extend(coord);
    });
    map.fitBounds(bounds);

  } catch (error) {
    console.error('Error drawing polygon:', error);
  }
};

// Handle post-save actions
const handlePostSave = (savedData) => {
  // Initialize or update the map after save
  if (map && savedData.boundaryWkt) {
    drawPolygon(savedData.boundaryWkt);
  } else {
    initMap();
  }
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Load sample GeoJSON
const loadSampleGeoJson = () => {
  // Sample GeoJSON for a simple polygon (representing a small area)
  const sampleGeoJson = {
    "type": "Feature",
    "properties": {
      "name": "Sample Area"
    },
    "geometry": {
      "type": "Polygon",
      "coordinates": [
        [
          [-63.13, 46.25],
          [-63.13, 46.26],
          [-63.12, 46.26],
          [-63.12, 46.25],
          [-63.13, 46.25]
        ]
      ]
    }
  };

  geojsonInput.value = JSON.stringify(sampleGeoJson, null, 2);
};

// Pretty print JSON
const prettyPrintJson = (json) => {
  try {
    if (!json) return 'No boundary data';

    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If not valid JSON, return as is
      return json;
    }
  } catch (error) {
    return 'Invalid JSON';
  }
};

// Convert GeoJSON to WKT
const validateGeoJson = () => {
  if (!geojsonInput.value) {
    showToast({
      title: 'Error',
      body: 'Please enter valid GeoJSON data.',
      type: notificationType.ERROR,
      delay: 7000
    });
    return;
  }

  try {
    // Parse the GeoJSON
    const geojson = JSON.parse(geojsonInput.value);

    // Check if it's a valid GeoJSON
    if (!geojson || !geojson.type) {
      // Show error if GeoJSON is invalid
      return;
    }

    let coordinates = [];

    // Extract coordinates based on GeoJSON type
    if (geojson.type === 'Feature' && geojson.geometry) {
      // Handle Feature
      if (geojson.geometry.type === 'Polygon') {
        coordinates = geojson.geometry.coordinates[0]; // Outer ring
      } else if (geojson.geometry.type === 'MultiPolygon') {
        coordinates = geojson.geometry.coordinates[0][0]; // First polygon, outer ring
      } else {
        showToast({
          title: 'Error',
          body: 'GeoJSON must contain a Polygon or MultiPolygon geometry',
          type: notificationType.ERROR,
          delay: 7000
        });
        return;
      }
    } else if (geojson.type === 'Polygon') {
      // Handle direct Polygon
      coordinates = geojson.coordinates[0]; // Outer ring
    } else if (geojson.type === 'MultiPolygon') {
      // Handle direct MultiPolygon
      coordinates = geojson.coordinates[0][0]; // First polygon, outer ring
    } else {
      showToast({
        title: 'Error',
        body: 'GeoJSON must be a Feature with Polygon/MultiPolygon or a direct Polygon/MultiPolygon',
        type: notificationType.ERROR,
        delay: 7000
      });
      return;
    }

    if (!coordinates || coordinates.length < 3) {
      showToast({
        title: 'Error',
        body: 'Polygon must have at least 3 coordinates',
        type: notificationType.ERROR,
        delay: 7000
      });
      return;
    }

    // Convert coordinates to WKT format
    // WKT POLYGON format: POLYGON((lng1 lat1, lng2 lat2, ...))
    const wktCoordinates = coordinates.map(coord => `${coord[0]} ${coord[1]}`).join(',');
    const wkt = `POLYGON((${wktCoordinates}))`;

    // Update the boundaryWkt field
    geoZone.value.state.boundaryWkt = wkt;

    // Update the map
    if (map) {
      drawPolygon(wkt);
    }
    showToast({
      title: 'Awesome',
      body: 'GeoJSON successfully converted to WKT',
      type: notificationType.SUCCESS
    });
  } catch (error) {
    console.error('Error validating GeoJSON:', error);
    showToast({
      title: 'Error',
      body: 'Error processing GeoJSON: ' + error.message,
      type: notificationType.ERROR,
    });
  }
};

// Load Google Maps API
const loadGoogleMapsApi = () => {
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${props.googleMapsApiKey}&loading=async&callback=initGoogleMaps`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);

  // Define the callback function
  window.initGoogleMaps = () => {
    if (!isNewZone.value && geoZone.value && geoZone.value.state) {
      initMap();
    }
  };
};

// Watch for changes in the boundary data to update the map
watch(() => geoZone.value?.state?.boundaryWkt, (newValue) => {
  if (map && newValue && isEditingGeoZone.value) {
    drawPolygon(newValue);
  }
});

onMounted(() => {
  loadGoogleMapsApi();
});
</script>
